import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, isNetworkError } from "../../lib/api";
import { toast } from "sonner";
import { Package, Users, Building2, Inbox, ListChecks, Flame, Boxes, ArrowRight, History } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import OfflineUnavailable from "../../components/system/OfflineUnavailable";

const ACCENTS = {
  expired:   "border-l-4 border-status-expired",
  urgent:    "border-l-4 border-status-urgent",
  attention: "border-l-4 border-status-soon",
  fresh:     "border-l-4 border-status-fresh",
};

function StatCard({ label, value, to, accent, testid, icon: Icon }) {
  const accentClass = ACCENTS[accent] || "";
  return (
    <Link to={to} className="group block" data-testid={`${testid}-link`}>
      <Card
        interactive
        className={`p-5 h-full ${accentClass}`}
        data-testid={testid}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="data-sku">{label}</div>
          {Icon && <Icon size={18} className="text-muted-foreground" />}
        </div>
        <div className="flex items-end justify-between">
          <div className="font-display font-black text-4xl tracking-tighter leading-none">
            {value ?? "—"}
          </div>
          <ArrowRight
            size={18}
            className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>
      </Card>
    </Link>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [stats, setStats] = useState(null);
  const [unavailableOffline, setUnavailableOffline] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
      setUnavailableOffline(false);
    } catch (e) {
      // Offline & no cache → render OfflineUnavailable; never show fake
      // "—" stat cards that look like real data, never spam Network
      // Error toasts when the user already knows they're offline.
      if (isNetworkError(e)) {
        setStats(null);
        setUnavailableOffline(true);
      } else if (e?.response) {
        toast.error(formatApiErrorDetail(e));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div data-testid={isManager ? "manager-dashboard" : "admin-dashboard"}>
      <PageHeader
        eyebrow={isManager ? "Store operations" : "Control room"}
        title={isManager ? "Store Manager" : "Dashboard"}
        subtitle={
          isManager
            ? undefined
            : "Tap any card to drill in."
        }
      />
      {isManager && (
        <p className="text-base text-foreground/80 font-display mb-6 -mt-2">
          Assigned Store:{" "}
          <span className="font-bold" data-testid="manager-dashboard-store-name">
            {user?.store_name || "—"}
          </span>
        </p>
      )}

      {!loading && unavailableOffline && (
        <OfflineUnavailable
          title="Dashboard unavailable offline"
          description="Live store metrics need a network connection. Reconnect to refresh your dashboard — your inventory and removal data are still safe."
          onRetry={load}
          data-testid="dashboard-offline-unavailable"
        />
      )}

      {!unavailableOffline && (
        <>
      <div className="mb-4 data-sku">Expiration status{isManager ? " · this store" : ""}</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Expired" value={stats?.inventory?.expired} to="/admin/inventory?u=expired" icon={Flame} testid="stat-expired" accent="expired" />
        <StatCard label="Urgent (≤3d)" value={stats?.inventory?.urgent} to="/admin/inventory?u=urgent" testid="stat-urgent" accent="urgent" />
        <StatCard label="Soon (4–7d)" value={stats?.inventory?.attention} to="/admin/inventory?u=attention" testid="stat-attention" accent="attention" />
        <StatCard label="Fresh" value={stats?.inventory?.fresh} to="/admin/inventory?u=fresh" testid="stat-fresh" accent="fresh" />
      </div>

      <div className="mb-4 data-sku">Operations</div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Active batches" value={stats?.inventory?.total} to="/admin/inventory" testid="stat-batches" icon={Boxes} />
        <StatCard label="Pending requests" value={stats?.pending_requests} to="/admin/requests" testid="stat-pending" icon={Inbox} />
        {!isManager && (
          <StatCard label="Open review items" value={stats?.open_review_items} to="/admin/review-queue" testid="stat-review" icon={ListChecks} />
        )}
        <StatCard
          label={isManager ? "Active employees" : "Users"}
          value={stats?.users}
          to="/admin/users"
          testid="stat-users"
          icon={Users}
        />
        {!isManager && (
          <>
            <StatCard label="Catalog" value={stats?.products} to="/admin/products" testid="stat-products" icon={Package} />
            <StatCard label="Departments" value={stats?.departments} to="/admin/departments" testid="stat-departments" icon={Building2} />
          </>
        )}
        {isManager && (
          <StatCard label="Recent activity" value={null} to="/admin/history" testid="stat-history" icon={History} />
        )}
      </div>
        </>
      )}
    </div>
  );
}
